// ** React Imports
import { Fragment, useState, useRef, useEffect } from 'react'

// ** Vertical Menu Items Array
import { useVerticalLayout } from '@navigation/vertical'

// ** Third Party Components
import classnames from 'classnames'
import PerfectScrollbar from 'react-perfect-scrollbar'

// ** Vertical Menu Components
import VerticalMenuHeader from './VerticalMenuHeader'
import VerticalNavMenuItems from './VerticalNavMenuItems'
import { Link } from 'react-router-dom'
import { IconUserPlus, IconFileText, IconAlertCircle } from '@tabler/icons-react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { Row } from 'reactstrap'
import ContributorModal from '../../../../../views/_templates/ContributorModal'
import ArcRequestModal from '../../../../../views/_templates/ArcRequestModal'
import { Role } from '../../../../../utility/types'
import { releaseNotes } from '../../../../../configs/constants'

const StyledHr = styled.hr`
  font-weight: bold;
  left: 10px;
  position: absolute;
  right: 10px;
  border-top: 2px solid var(--theme-border) !important;
  bottom: ${(props) => (props.$isAdmin ? '5rem' : '7.5rem')};
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  left: 1.7rem;
  bottom: 1rem;
  margin: 0 15px;
  font-size: 1.1rem;
  cursor: pointer;
  color: var(--color-primary);

  & > div:not(:last-child) {
    margin-bottom: 1rem;
  }
`
const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: absolute;
  left: 2rem;
  right: 1rem;
  bottom: 1rem;
  margin: 0 5px;

  & > div:not(:last-child) {
    margin-bottom: 1rem;
  }
`

const Sidebar = (props) => {
  const isBannerVisible = useSelector(
    (state) => state.notificationContextReducer.scheduleNotificationContext?.isBannerVisible
  )

  // State to track notification bar height
  const [notificationBarHeight, setNotificationBarHeight] = useState(0)

  useEffect(() => {
    if (isBannerVisible) {
      // Small delay to ensure element is rendered
      setTimeout(() => {
        const notificationBar = document.getElementById('schedule-notification')
        if (notificationBar) {
          const height = notificationBar.offsetHeight || 0
          setNotificationBarHeight(height)
        }
      }, 100)
    } else {
      setNotificationBarHeight(0)
    }
  }, [isBannerVisible])

  const getCustomizerStyle = () => {
    if (isBannerVisible && notificationBarHeight > 0) {
      return {
        borderRight: 'none',
        zIndex: 12,
        position: 'fixed',
        height: `calc(100% - ${notificationBarHeight}px - 10px)`, // subtract 10px to prevent sidebar overflow
        ...(isBannerVisible && { marginBottom: `${notificationBarHeight}px` })
      }
    } else {
      return {
        borderRight: 'none',
        zIndex: 12
      }
    }
  }

  const { roles } = useSelector((state) => state.userContextReducer.userContext)
  const isContributor = roles?.includes(Role.Contributor)
  const isAdmin = roles?.includes(Role.Admin)

  // ** Props
  const { menuCollapsed, routerProps, menu, currentActiveItem, skin } = props
  const { verticalNavItems } = useVerticalLayout()

  // ** States
  const [groupOpen, setGroupOpen] = useState([])
  const [groupActive, setGroupActive] = useState([])
  const [activeItem, setActiveItem] = useState(null)
  const [contributorModal, setContributorModal] = useState(false)
  const [isArcRequestModalOpen, setIsArcRequestModalOpen] = useState(false)

  // ** Menu Hover State
  const [menuHover, setMenuHover] = useState(false)

  // ** Ref
  const shadowRef = useRef(null)

  // ** Function to handle Mouse Enter
  const onMouseEnter = () => {
    if (menuCollapsed) {
      setMenuHover(true)
    }
  }

  const handleToggleArcRequestModal = () => {
    setIsArcRequestModalOpen((prevState) => !prevState)
    setContributorModal((prevState) => !prevState)
  }

  // ** Scroll Menu
  const scrollMenu = (container) => {
    if (shadowRef && container.scrollTop > 0) {
      if (!shadowRef.current.classList.contains('d-block')) {
        shadowRef.current.classList.add('d-block')
      }
    } else {
      if (shadowRef.current.classList.contains('d-block')) {
        shadowRef.current.classList.remove('d-block')
      }
    }
  }

  return (
    <Fragment>
      <div
        className={classnames('main-menu menu-fixed menu-accordion menu-shadow entireSideBar', {
          expanded: menuHover || menuCollapsed === false,
          'menu-light': skin !== 'semi-dark' && skin !== 'dark',
          'menu-dark': skin === 'semi-dark' || skin === 'dark'
        })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={() => setMenuHover(false)}
        style={getCustomizerStyle()}>
        {menu ? (
          menu(props)
        ) : (
          <Fragment>
            {/* Vertical Menu Header */}
            <VerticalMenuHeader setGroupOpen={setGroupOpen} menuHover={menuHover} {...props} />
            {/* Vertical Menu Header Shadow */}
            <div className="shadow-bottom" ref={shadowRef}></div>
            {/* Perfect Scrollbar */}
            <PerfectScrollbar
              className={`main-menu-content ${isAdmin ? 'admin' : ''} pt-2`}
              options={{ wheelPropagation: false }}
              onScrollY={(container) => scrollMenu(container)}>
              <ul className="navigation navigation-main">
                <VerticalNavMenuItems
                  items={verticalNavItems}
                  groupActive={groupActive}
                  setGroupActive={setGroupActive}
                  activeItem={activeItem}
                  setActiveItem={setActiveItem}
                  groupOpen={groupOpen}
                  setGroupOpen={setGroupOpen}
                  routerProps={routerProps}
                  menuCollapsed={menuCollapsed}
                  menuHover={menuHover}
                  currentActiveItem={currentActiveItem}
                />
              </ul>
            </PerfectScrollbar>
            <StyledHr $isAdmin={isAdmin} />

            {menuCollapsed && !menuHover ? (
              <IconWrapper>
                {!isAdmin && (
                  <div>
                    <IconUserPlus size={20} />
                  </div>
                )}
                <div>
                  <IconAlertCircle size={20} />
                </div>
                <div>
                  <IconFileText size={20} />
                </div>
              </IconWrapper>
            ) : (
              <ButtonWrapper>
                {!isAdmin && (
                  <div onClick={() => setContributorModal(!contributorModal)} className="d-flex">
                    <IconUserPlus size={20} />
                    &nbsp; &nbsp;{isContributor ? "You're a Contributor" : 'Contributor guide'}
                  </div>
                )}
                <Row>
                  <Link to="/landing/home" className="flex">
                    <IconAlertCircle size={20} />
                    &nbsp; &nbsp;About Centrali
                  </Link>
                </Row>
                <Row>
                  <a href={releaseNotes} target="_blank" rel="noreferrer" className="flex">
                    <IconFileText size={20} />
                    &nbsp; &nbsp;Release notes
                  </a>
                </Row>
              </ButtonWrapper>
            )}
          </Fragment>
        )}
      </div>

      {contributorModal && (
        <ContributorModal
          isOpen={contributorModal}
          toggle={() => setContributorModal(!contributorModal)}
          handleToggleArcRequestModal={handleToggleArcRequestModal}
          isContributor={isContributor}
        />
      )}
      {isArcRequestModalOpen && (
        <ArcRequestModal
          isOpen={isArcRequestModalOpen}
          toggle={() => setIsArcRequestModalOpen(false)}
          isContributorReq={true}
        />
      )}
    </Fragment>
  )
}

export default Sidebar
